import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, X, Plus, Building2, Phone, Mail, MapPin, User, Loader2,
  Pencil, Trash2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';

export interface Supplier {
  id: number;
  name: string;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
}

interface SupplierManagerProps {
  storeApiKey: string;
  onBack: () => void;
}

const BLANK: Omit<Supplier, 'id'> = { name: '', contactPerson: '', phone: '', email: '', address: '' };

export const SupplierManager: React.FC<SupplierManagerProps> = ({ storeApiKey, onBack }) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<Supplier | null>(null);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  const headers = { 'x-store-api-key': storeApiKey, 'Content-Type': 'application/json' };

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadSuppliers = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/store/suppliers', { headers });
      if (res.ok) setSuppliers(await res.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadSuppliers(); }, []);

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...BLANK });
    setError('');
    setShowForm(true);
  };

  const openEdit = (s: Supplier) => {
    setEditTarget(s);
    setForm({ name: s.name, contactPerson: s.contactPerson || '', phone: s.phone || '', email: s.email || '', address: s.address || '' });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Supplier name is required'); return; }
    setSaving(true);
    try {
      const url = editTarget ? `/api/store/suppliers/${editTarget.id}` : '/api/store/suppliers';
      const method = editTarget ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      setShowForm(false);
      await loadSuppliers();
      showToast(editTarget ? 'Supplier updated' : 'Supplier added');
    } catch { setError('Cannot reach server'); }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`/api/store/suppliers/${id}`, { method: 'DELETE', headers });
      if (res.ok) { setSuppliers(prev => prev.filter(s => s.id !== id)); showToast('Supplier deleted'); }
      else showToast('Delete failed', false);
    } catch { showToast('Cannot reach server', false); }
    setDeleteConfirmId(null);
  };

  const f = (k: keyof typeof form, v: string) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-[50] bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-sky-100 bg-white shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors shrink-0"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-xl bg-sky-100 border border-sky-200 flex items-center justify-center shrink-0">
            <Building2 size={16} className="text-sky-600" />
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">Suppliers</h2>
            <p className="text-[9px] text-slate-500 font-mono">{suppliers.length} registered</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <motion.button whileTap={{ scale: 0.96 }} onClick={openCreate}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-[11px] font-bold shadow-sm transition-colors">
            <Plus size={13} /> Add Supplier
          </motion.button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-sky-400" /></div>
        ) : suppliers.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={36} className="mx-auto mb-3 text-slate-300" />
            <p className="text-[14px] font-bold text-slate-500 mb-1">No Suppliers Yet</p>
            <p className="text-[11px] text-slate-400 mb-4">Add your first supplier to link products to vendors.</p>
            <motion.button whileTap={{ scale: 0.97 }} onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500 text-white text-[12px] font-bold">
              <Plus size={13} /> Add Your First Supplier
            </motion.button>
          </div>
        ) : (
          <div className="space-y-3 max-w-2xl">
            {suppliers.map(s => (
              <motion.div key={s.id} layout
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center shrink-0">
                      <Building2 size={16} className="text-sky-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-black text-slate-800 truncate">{s.name}</p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">{s.contactPerson || s.phone || s.email || 'No contact info'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}
                      className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 transition-colors">
                      {expandedId === s.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <button onClick={() => openEdit(s)}
                      className="w-8 h-8 rounded-lg hover:bg-sky-50 hover:text-sky-600 flex items-center justify-center text-slate-400 transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => setDeleteConfirmId(s.id)}
                      className="w-8 h-8 rounded-lg hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center text-slate-400 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedId === s.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-100 px-4 py-3 space-y-1.5 overflow-hidden">
                      {s.contactPerson && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-600">
                          <User size={11} className="text-slate-400 shrink-0" />{s.contactPerson}
                        </div>
                      )}
                      {s.phone && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-600">
                          <Phone size={11} className="text-slate-400 shrink-0" />{s.phone}
                        </div>
                      )}
                      {s.email && (
                        <div className="flex items-center gap-2 text-[11px] text-slate-600">
                          <Mail size={11} className="text-slate-400 shrink-0" />{s.email}
                        </div>
                      )}
                      {s.address && (
                        <div className="flex items-start gap-2 text-[11px] text-slate-600">
                          <MapPin size={11} className="text-slate-400 shrink-0 mt-0.5" /><span>{s.address}</span>
                        </div>
                      )}
                      {!s.contactPerson && !s.phone && !s.email && !s.address && (
                        <p className="text-[10px] text-slate-400 italic">No additional contact info</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Delete confirm */}
                <AnimatePresence>
                  {deleteConfirmId === s.id && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="border-t border-rose-100 bg-rose-50 px-4 py-3 flex items-center justify-between overflow-hidden">
                      <p className="text-[11px] font-bold text-rose-700">Delete "{s.name}"? Products will be unlinked.</p>
                      <div className="flex gap-2">
                        <button onClick={() => setDeleteConfirmId(null)}
                          className="px-3 py-1.5 rounded-lg text-[10px] font-bold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
                        <button onClick={() => handleDelete(s.id)}
                          className="px-3 py-1.5 rounded-lg text-[10px] font-bold bg-rose-500 text-white hover:bg-rose-600 transition-colors">Delete</button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <motion.div initial={{ scale: 0.95, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h3 className="text-[15px] font-black text-slate-800 flex items-center gap-2">
                  <Building2 size={15} className="text-sky-500" />
                  {editTarget ? 'Edit Supplier' : 'Add New Supplier'}
                </h3>
                <button onClick={() => setShowForm(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400"><X size={15} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Supplier / Company Name *</label>
                  <input value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Saman Auto Suppliers" required
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Contact Person</label>
                  <input value={form.contactPerson} onChange={e => f('contactPerson', e.target.value)} placeholder="Full name"
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Phone</label>
                    <input value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="07X XXX XXXX"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Email</label>
                    <input type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="supplier@email.com"
                      className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Address</label>
                  <textarea value={form.address} onChange={e => f('address', e.target.value)} placeholder="Supplier address…" rows={2}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 resize-none" />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-[11px] text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2 rounded-xl">
                    <AlertTriangle size={12} /> {error}
                  </div>
                )}
                <div className="flex gap-3 pt-1">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-[12px] font-bold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
                  <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={saving}
                    className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-black flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    {saving ? 'Saving…' : editTarget ? 'Update Supplier' : 'Add Supplier'}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-3 rounded-2xl text-[12px] font-bold shadow-lg border z-[9999]
              ${toast.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
            {toast.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}{toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
